import { _ } from "../../../core/polyglot/polyglot.js";
import { iconAndText, spacer } from "../../../core/elements/index.js";
import { sortIcon } from "../../../core/elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import makeDragAndDropRenderer from "../../../core/dragAndDrop/makeDragAndDropRenderer.js";
import popoverActionButtonWidget from "../../../core/widgets/popoverActionButtonWidget.js";
import SwitchButton from "../../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * Section editor for a period report draft.
 *
 * It renders a picker to be displayed in the right-hand menu to reorder and
 * enable/disable individual sections.
 *
 * @param {periodReport} spec.periodReport - Period report for which the sections
 * are edited.
 */
export default class PeriodReportSectionPickerWidget extends Widget {
	constructor({ periodReport } = {}) {
		super(...arguments);
		this._periodReport = periodReport;
		this._sectionSpecs = [];
		this.sectionsChanged = this._events.createEvent();

		this._popoverButton = popoverActionButtonWidget({
			label: _("Report sections"),
			popoverHeader: _("Select & sort sections"),
			popoverContent: this._sectionsRenderer(),
			dataTestName: "report-sections",
			popoverAction: this._updatePeriodReportSections.bind(this),
			cancelAction: this._resetSectionItems.bind(this),
		});
	}

	setPeriodReport(periodReport) {
		this._periodReport = periodReport;
		this._resetSectionItems();
		this.update();
	}

	renderContentOn(html) {
		html.render(this._popoverButton);
	}

	_sectionsRenderer() {
		return makeDragAndDropRenderer({
			getItems: () => this._sectionSpecs,
			itemRenderer: this._itemRenderer.bind(this),
			onChange: (items) => (this._sectionSpecs = items),
			container: this,
		});
	}

	_itemRenderer(sectionSpec) {
		return spacer(
			{ spacing: "between" },
			this._nameRenderer(sectionSpec),
			this._switchRenderer(sectionSpec),
		);
	}

	_nameRenderer(sectionSpec) {
		return iconAndText(
			{ icon: sortIcon, class: sectionSpec.id },
			sectionSpec.title,
		);
	}

	_switchRenderer(sectionSpec) {
		const switchButton = new SwitchButton({
			state: sectionSpec.isEnabled,
			dataTestName: "section-switch",
		});

		switchButton.stateChanged.register(
			(state) => (sectionSpec.isEnabled = state),
		);

		return switchButton;
	}

	_updatePeriodReportSections() {
		const updatedSections = this._sectionSpecs.map(({ id, isEnabled }) => {
			const section = this._periodReport.findSectionWithId(id);
			section.setEnabled(isEnabled);
			return section;
		});

		this._periodReport.setSections(updatedSections);
		this.sectionsChanged.trigger();
	}

	_resetSectionItems() {
		this._sectionSpecs = this._periodReport
			.getSections()
			.map(this._makeSectionSpec.bind(this));
	}

	_makeSectionSpec(section) {
		return {
			id: section.getId(),
			title: section.getTitle(),
			isEnabled: section.isEnabled(),
		};
	}
}
